import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Check, Copy, MoreHorizontal, X } from "lucide-react";
import { Link, useOutletContext } from "react-router-dom";
import type { AuthUser } from "@match/shared";
import { parseDecimal } from "@/lib/units";

/*
 * Primitive condivise dalle pagine.
 *
 * I pulsanti primari sono del MARCHIO (`--accent`), non indaco o ambra: indaco
 * e ambra significano prop e broker, e usarli come colore d'azione diluirebbe
 * l'informazione. Senza un marchio di tenant `--accent` vale come `--ink`
 * (index.css), quindi qui restano neutri come prima.
 */

// ---------------------------------------------------------------------------
// Utente e permessi
// ---------------------------------------------------------------------------

export function useCurrentUser(): AuthUser {
  return useOutletContext<AuthUser>();
}

/**
 * Sta guardando un CLIENTE la sua area, e non un operatore il gestionale.
 *
 * Serve alle pagine condivise — i cicli — per non mettergli in vista maniglie
 * che non girano: «Scheda cliente», «Apri la fase», «Nuova fase» portano a
 * rotte che per lui sono 403. **Non è una barriera di sicurezza**: quella sta
 * nel backend (`lib/client-scope.ts`), allowlist e filtro dentro la query.
 */
export function useIsClient(): boolean {
  return useCurrentUser().role === "CLIENT";
}

/**
 * Solo ADMIN, come `requireRole(["ADMIN"])` lato backend: il CID del cliente,
 * gli accessi al portale, l'eliminazione di un server.
 *
 * **Le credenziali MT5 non passano piu' di qui**: le vedono tutti gli interni,
 * e il confine e' il CLIENT (`useIsClient`).
 */
export function useIsAdmin(): boolean {
  return useCurrentUser().role === "ADMIN";
}

/** Scrittura: OPERATORE e ADMIN, come requireRole lato backend. */
export function useCanWrite(): boolean {
  const user = useCurrentUser();
  return user.role === "ADMIN" || user.role === "OPERATORE";
}

// ---------------------------------------------------------------------------
// Pulsanti e campi
// ---------------------------------------------------------------------------

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

const BUTTON: Record<ButtonVariant, string> = {
  primary: "bg-accent text-on-accent hover:bg-accent/90",
  secondary: "border border-line bg-secondary text-ink hover:border-muted/60",
  ghost: "text-muted hover:bg-raised hover:text-ink",
  danger: "border border-loss/40 text-loss hover:bg-loss/10",
};

export function Button({
  variant = "secondary",
  size = "md",
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: "sm" | "md" }) {
  return (
    <button
      type="button"
      {...props}
      className={[
        "inline-flex items-center justify-center gap-1.5 rounded font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        size === "sm" ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm",
        BUTTON[variant],
        className,
      ].join(" ")}
    />
  );
}

export const inputClass =
  "w-full rounded border border-line bg-raised px-3 py-2 text-sm text-ink outline-none placeholder:text-faint focus:border-muted disabled:opacity-60";

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm text-muted">
        {label}
      </label>
      {children(id)}
      {hint && <p className="mt-1 text-xs text-faint">{hint}</p>}
    </div>
  );
}

/**
 * Un campo numerico dei moduli: "0,2" e "0.2" valgono uguale, il resto è un
 * errore scritto SOTTO IL CAMPO (`parseDecimal`, l'unica lettura). Il campo
 * tiene il testo com'è: il numero col punto lo ricava chi manda la richiesta,
 * dalla stessa funzione. Mentre si scrive "0," il campo non grida: è un numero
 * a metà, non un errore — ma vale come errore per chi lo legge.
 */
export function DecimalField({
  label,
  value,
  onChange,
  suffix,
  hint,
  after,
  className = "",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  suffix?: string;
  hint?: ReactNode;
  after?: ReactNode;
  className?: string;
}) {
  const [focused, setFocused] = useState(false);
  const parsed = parseDecimal(value);
  const typing = focused && /^\d+[.,]$/.test(value.trim());
  const error = parsed.kind === "error" && !typing ? parsed.message : null;
  const errId = useId();
  return (
    <Field label={label} hint={hint}>
      {(id) => (
        <div>
          <div className={`relative ${className}`}>
            <input
              id={id}
              inputMode="decimal"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errId : undefined}
              className={`${inputClass} ${suffix ? "pr-12" : ""} ${error ? "border-loss focus:border-loss" : ""}`}
            />
            {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>}
          </div>
          {error && (
            <p id={errId} role="alert" className="mt-1 text-xs text-loss">
              {error}
            </p>
          )}
          {after}
        </div>
      )}
    </Field>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p role="alert" className="rounded border border-loss/40 bg-loss/10 px-3 py-2 text-sm text-loss">
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Spiegazioni fuori dal testo
// ---------------------------------------------------------------------------

/**
 * Il «?» accanto a un'etichetta: la spiegazione sta lì, non in un paragrafo
 * sotto i dati. Si apre al passaggio del mouse (`group-hover`, senza JS) e
 * resta apribile al clic/tocco per chi il mouse non ce l'ha — tastiera,
 * schermo touch — e allora si chiude perdendo il fuoco.
 */
export function Hint({ children, label = "Spiegazione" }: { children: ReactNode; label?: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <span className="group/hint relative ml-1 inline-flex align-middle">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setOpen(false)}
        aria-expanded={open}
        aria-describedby={id}
        aria-label={label}
        className="flex h-3.5 w-3.5 items-center justify-center rounded-full border border-line text-2xs leading-none text-faint hover:border-muted hover:text-ink"
      >
        ?
      </button>
      <span
        id={id}
        role="tooltip"
        className={`absolute bottom-full left-1/2 z-10 mb-1.5 w-56 -translate-x-1/2 rounded border border-line bg-surface px-2.5 py-1.5 text-xs font-normal normal-case text-muted shadow-lg ${
          open ? "block" : "hidden group-hover/hint:block"
        }`}
      >
        {children}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Numeri chiave
// ---------------------------------------------------------------------------

export interface KeyFigure {
  label: string;
  /** Già formattato (`lib/format`): valore e valuta insieme, come si scrivono in una riga. */
  value: ReactNode;
  /** Spiegazione, aperta dal "?"; sostituisce il paragrafo che prima stava sotto. */
  hint?: ReactNode;
  /** Colore del valore; default `text-ink`. */
  tone?: string;
}

/**
 * La riga dei numeri chiave in cima a una pagina di dettaglio: 3 o 4 riquadri,
 * ognuno con la sua domanda. Non è per un elenco lungo — quello è `DataTable`.
 */
export function KeyFigures({ items }: { items: KeyFigure[] }) {
  return (
    <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((it) => (
        <div key={it.label} className="card p-4">
          <p className="flex items-center text-xs text-faint">
            {it.label}
            {it.hint && <Hint>{it.hint}</Hint>}
          </p>
          <p className={`mt-1 text-lg font-medium ${it.tone ?? "text-ink"}`}>{it.value}</p>
        </div>
      ))}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Pannello laterale
// ---------------------------------------------------------------------------

export function SidePanel({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-page/70" onClick={onClose} aria-hidden />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex h-full w-full max-w-md flex-col border-l border-line bg-surface shadow-2xl"
      >
        <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div>
            <h2 className="text-base font-semibold tracking-tight">{title}</h2>
            {subtitle && <p className="mt-0.5 text-sm text-muted">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="rounded p-1 text-muted hover:bg-raised hover:text-ink" aria-label="Chiudi">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">{footer}</footer>}
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Valori
// ---------------------------------------------------------------------------

/** Tratto per "non applicabile". */
export function Dash({ title }: { title?: string }) {
  return (
    <span className="text-faint" title={title}>
      —
    </span>
  );
}

/** Valore rilevato dal gateway: corsivo e grigio. Quelli impostati a mano restano in tondo. */
export function Auto({ children, title = "Rilevato dal gateway" }: { children: ReactNode; title?: string }) {
  return (
    <span className="italic text-muted" title={title}>
      {children}
    </span>
  );
}

export function ConnectionState({
  connected,
  error,
  suspendedReason,
  compact = false,
}: {
  connected: boolean;
  error?: string | null;
  /** «Collegamento sospeso»: valorizzato = il conto non si ricollega da solo finche' non si preme Connetti. */
  suspendedReason?: string | null;
  compact?: boolean;
}) {
  if (!connected && suspendedReason) {
    const testo = `Collegamento sospeso: ${suspendedReason}. Premi Connetti per riprovare`;
    return (
      <span className="inline-flex items-center gap-1.5 text-sm" title={testo}>
        <span className="h-2 w-2 shrink-0 rounded-full bg-loss" aria-hidden />
        {compact ? <span className="sr-only">{testo}</span> : <span className="text-loss">Collegamento sospeso</span>}
      </span>
    );
  }
  const tone = connected ? "bg-gain" : error ? "bg-loss" : "bg-faint";
  // Con un motivo (sessione assente sul gateway, connessione fallita) e' «Disconnesso» in rosso, e il motivo sta nel title.
  // Senza motivo lo scollegamento e' nostro (inattivo, coppia chiusa, mai acceso): grigio, non rosso.
  const label = connected ? "Connesso" : error ? "Disconnesso" : "Scollegato";
  return (
    <span className="inline-flex items-center gap-1.5 text-sm" title={error ?? label}>
      <span className={`h-2 w-2 shrink-0 rounded-full ${tone}`} aria-hidden />
      {compact ? (
        <span className="sr-only">{error ? `${label}: ${error}` : label}</span>
      ) : (
        <span className={connected ? "text-ink" : error ? "text-loss" : "text-muted"}>{label}</span>
      )}
    </span>
  );
}

export const COMPUTER_ID_RE = /^[0-9a-fA-F]{32}$/;

// ---------------------------------------------------------------------------
// Modifica in linea
// ---------------------------------------------------------------------------

/**
 * Cella modificabile: si clicca, si scrive, Invio o uscita salvano, Esc annulla.
 * `display` è ciò che si vede a riposo; `initial` il testo da cui parte l'editor.
 */
export function InlineEdit({
  display,
  initial,
  onSave,
  disabled,
  placeholder,
  inputMode,
  ariaLabel,
}: {
  display: ReactNode;
  initial: string;
  onSave: (value: string) => Promise<unknown>;
  disabled?: boolean;
  placeholder?: string;
  inputMode?: "text" | "numeric";
  ariaLabel: string;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) ref.current?.select();
  }, [editing]);

  if (disabled) return <>{display}</>;

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setValue(initial);
          setError(null);
          setEditing(true);
        }}
        className="-mx-1 max-w-full rounded px-1 text-left hover:bg-raised"
        aria-label={ariaLabel}
        title={error ?? "Clicca per modificare"}
      >
        {display}
      </button>
    );
  }

  const commit = async () => {
    if (value === initial) return setEditing(false);
    setBusy(true);
    try {
      await onSave(value);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Salvataggio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="block">
      <input
        ref={ref}
        value={value}
        disabled={busy}
        inputMode={inputMode}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") void commit();
          if (e.key === "Escape") setEditing(false);
        }}
        className="w-full min-w-24 rounded border border-muted bg-raised px-1.5 py-0.5 text-sm outline-none"
      />
      {error && <span className="mt-0.5 block text-xs text-loss">{error}</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Menu "…"
// ---------------------------------------------------------------------------

/** Dentro un menu «…» i componenti a più passi (Elimina, Archivia) si mettono a tutta larghezza, come le altre voci. */
const InMenuContext = createContext(false);
export const useInMenu = () => useContext(InMenuContext);

/** Una voce del menu «…» che porta a un'altra pagina. */
export function MenuLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} role="menuitem" className="block rounded px-2 py-1.5 text-sm text-ink hover:bg-raised">
      {children}
    </Link>
  );
}

/**
 * Il menu "…" condiviso, UNO SOLO (prima ce n'erano tre copie).
 *
 * Il pannello sta in un portale sul `body` e si posiziona da solo con
 * `position: fixed` accanto al pulsante — si apre sotto, o sopra se sotto non c'è
 * posto, e resta dentro la finestra —: una tabella con `overflow` non lo taglia
 * più. Si chiude al clic fuori o con Esc — **mai a un clic dentro**. Dentro ci
 * vivono flussi a più passi (una conferma, un'anteprima che arriva dal backend):
 * chiudersi al primo clic interromperebbe esattamente quel passo. Per lo stesso
 * motivo non è un `DropdownMenu` con la sua gestione della tastiera e del fuoco,
 * pensata per voci che eseguono e chiudono.
 *
 * Tutte le voci hanno lo stesso allineamento: i pulsanti dentro il pannello sono
 * a tutta larghezza, a sinistra, con lo stesso passo delle voci-link.
 */
export function MoreMenu({ children, label = "Altre azioni" }: { children: ReactNode; label?: string }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const trigger = useRef<HTMLSpanElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const t = trigger.current;
    const p = panel.current;
    if (!t || !p) return;
    const r = t.getBoundingClientRect();
    const w = p.offsetWidth;
    const h = p.offsetHeight;
    const gap = 4;
    const margin = 8;
    const left = Math.min(Math.max(margin, r.right - w), Math.max(margin, window.innerWidth - w - margin));
    const sotto = r.bottom + gap;
    const sopra = r.top - gap - h;
    const entraSotto = sotto + h + margin <= window.innerHeight;
    const top = entraSotto ? sotto : sopra >= margin ? sopra : Math.max(margin, window.innerHeight - h - margin);
    setPos((old) => (old && old.top === top && old.left === left ? old : { top, left }));
  }, []);

  useLayoutEffect(() => {
    if (open) place();
    else setPos(null);
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const n = e.target as Node;
      if (!trigger.current?.contains(n) && !panel.current?.contains(n)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", place);
    // In cattura: lo scorrimento di un contenitore qualunque sposta il pulsante.
    window.addEventListener("scroll", place, true);
    // Le conferme dentro il pannello ne cambiano l'altezza.
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place);
    if (panel.current) ro?.observe(panel.current);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      ro?.disconnect();
    };
  }, [open, place]);

  return (
    <>
      <span ref={trigger} className="inline-block">
        <Button size="sm" variant="ghost" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen((o) => !o)}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </span>
      {open &&
        createPortal(
          <div
            ref={panel}
            role="menu"
            style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? "visible" : "hidden" }}
            className="fixed z-50 max-h-[calc(100vh-16px)] w-56 space-y-0.5 overflow-y-auto rounded border border-line bg-surface p-1.5 shadow-lg [&_button]:w-full [&_button]:justify-start [&_button]:px-2 [&_button]:py-1.5 [&_button]:text-left [&_button]:text-sm"
          >
            <InMenuContext.Provider value>{children}</InMenuContext.Provider>
          </div>,
          document.body,
        )}
    </>
  );
}

/** Copia un valore negli appunti. Per gli identificatori macchina: login, ticket, CID. */
export function CopyButton({ value, label }: { value: string; label: string }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      /* permesso negato: il valore resta selezionabile a mano */
    }
  };
  return (
    <button
      type="button"
      onClick={() => void copy()}
      className="rounded p-1 text-muted hover:bg-raised hover:text-ink"
      aria-label={`Copia ${label}`}
      title={done ? "Copiato" : `Copia ${label}`}
    >
      {done ? <Check className="h-3.5 w-3.5 text-gain" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}
